/**
 * useCategories Hook
 * ahooks data fetching hook for category list with filters, pagination, and sorting
 */

import { useRequest, useAntdTable } from 'ahooks';
import type { Options } from 'ahooks/lib/useRequest/src/types';
import { trackCacheKey, clearCacheWhere } from '@/utils/cacheKeyRegistry';
import { useCategoryStore, useCategoryQueryParams } from '@/stores/categoryStore';
import type {
  CategoryListResponse,
  CategoryDetailResponse,
  CategoryListParams,
} from '@/types/api/category';
import { categoryApi } from '@/services/api';

/**
 * Hook to fetch category list with automatic state management
 * Syncs with Zustand store for filters, pagination, and sorting
 *
 * @param options.activeOnly - If true, only fetches active categories (for selection dropdowns)
 *                            Also sets pageSize: 1000 to fetch ALL categories for dropdown usage
 */
export function useCategories(options?: {
  activeOnly?: boolean;
  config?: Options<CategoryListResponse, []>;
}) {
  const { activeOnly = false, config } = options ?? {};

  // Get query params from store (only used when NOT activeOnly)
  const storeParams = useCategoryQueryParams();
  const { setCategories, setPagination, setLoading, setError } = useCategoryStore();

  // When activeOnly is true (for dropdowns), use CLEAN params that ignore store filters
  // This prevents Categories page filters from polluting dropdown data
  const params = activeOnly
    ? { isActive: 1, pageSize: -1, page: 1 } // Clean params - ignore store filters for dropdowns
    : storeParams;

  // Fetch categories with ahooks
  const cacheKey = trackCacheKey(`/api/categories?${JSON.stringify(params)}`);
  const {
    data,
    error,
    loading,
    refresh: _refresh,
    run,
    mutate,
  } = useRequest(() => categoryApi.list(params), {
    refreshDeps: [JSON.stringify(params)],
    cacheKey,
    staleTime: 60000, // 1 minute
    cacheTime: 60000, // Cache expiration
    ...config,
    onSuccess: (data) => {
      // Only sync to store if not using activeOnly filter (store should reflect main list)
      if (!activeOnly) {
        setCategories(data.data);
        setPagination(data.pagination);
      }
      setLoading(false);
      setError(null);
      config?.onSuccess?.(data, []);
    },
    onError: (err) => {
      setLoading(false);
      setError(err.message || 'Failed to fetch categories');
      config?.onError?.(err, []);
    },
  });

  return {
    categories: data?.data ?? [],
    pagination: data?.pagination,
    error,
    isLoading: loading,
    mutate,
    refresh: () => {
      // Clear all category-related caches (list, detail, color map)
      invalidateAllCategoryCaches();
      // Force refresh by running the request again
      return run();
    },
  };
}

/**
 * Hook using useAntdTable for seamless Ant Design Table integration
 * Returns tableProps that can be spread directly onto <Table>
 *
 * @param filters - External filters (search, typeIds, isActive) from store or local state
 */
export function useCategoriesTable(filters: {
  search?: string;
  typeIds?: number[];
  isActive?: boolean | null;
}) {
  const { setCategories, setPagination } = useCategoryStore();

  // Convert filters to API format
  const getApiParams = (
    filters: (typeof useCategoriesTable.arguments)[0]
  ): Partial<CategoryListParams> => {
    let isActiveParam: number | undefined;
    if (filters.isActive === true) {
      isActiveParam = 1;
    } else if (filters.isActive === false) {
      isActiveParam = 2;
    } else {
      isActiveParam = 3; // null means all
    }

    return {
      search: filters.search || undefined,
      typeIds:
        filters.typeIds && filters.typeIds.length > 0 ? filters.typeIds.join(',') : undefined,
      isActive: isActiveParam,
    };
  };

  const { tableProps, refresh, loading, error, params } = useAntdTable(
    async ({ current, pageSize, sorter }) => {
      // Map sorter to API format
      let sortBy: number | undefined;
      let sortOrder: number | undefined;
      let clientSideSort: { field: string; order: 'ascend' | 'descend' } | undefined;

      if (sorter && 'field' in sorter && sorter.order) {
        const field = sorter.field as string;

        // categoryId sorting is handled client-side (API doesn't support it)
        if (field === 'categoryId') {
          clientSideSort = { field, order: sorter.order };
        } else {
          // API sort_by: 1=category, 2=type_id, 3=color
          const fieldToSortBy: Record<string, number> = {
            category: 1, // Sort by category name
            typeInfo: 2, // Sort by type
            color: 3, // Sort by color
          };
          sortBy = fieldToSortBy[field];
          sortOrder = sorter.order === 'descend' ? 1 : 0;
        }
      }

      const params: CategoryListParams = {
        page: current,
        pageSize,
        ...getApiParams(filters),
        sortBy,
        sortOrder,
      };

      const response = await categoryApi.list(params);

      // Apply client-side sorting for categoryId (API doesn't support it)
      let sortedData = response.data;
      if (clientSideSort?.field === 'categoryId') {
        sortedData = [...response.data].sort((a, b) => {
          const diff = a.categoryId - b.categoryId;
          return clientSideSort.order === 'ascend' ? diff : -diff;
        });
      }

      // Sync with Zustand store
      setCategories(sortedData);
      setPagination(response.pagination);

      return {
        total: response.pagination.total,
        list: sortedData,
      };
    },
    {
      defaultPageSize: 10,
      refreshDeps: [filters.search, JSON.stringify(filters.typeIds), filters.isActive],
    }
  );

  return {
    tableProps,
    refresh: () => {
      // Clear all category caches (list, detail, color map) and refresh
      invalidateAllCategoryCaches();
      refresh();
    },
    isLoading: loading || params.length === 0,
    error,
  };
}

/**
 * Hook to fetch category detail by ID
 */
export function useCategoryDetail(
  categoryId: number | null,
  config?: Options<CategoryDetailResponse, []>
) {
  const cacheKey = trackCacheKey(categoryId ? `/api/categories/${categoryId}` : undefined);
  const {
    data,
    error,
    loading,
    refresh: _refresh,
    run,
    mutate,
  } = useRequest(() => categoryApi.getById(categoryId!), {
    ready: !!categoryId,
    cacheKey,
    staleTime: 30000, // 30 seconds
    ...config,
  });

  return {
    category: data,
    error,
    isLoading: loading,
    mutate,
    refresh: () => {
      // Clear all category caches to ensure related components get fresh data
      invalidateAllCategoryCaches();
      // Force refresh by running the request again (bypasses cache)
      return run();
    },
  };
}

/**
 * Hook to fetch ALL categories for color mapping
 * Fetches all categories without pagination limits
 * Independent of category store filters - for list/detail page color mapping
 *
 * @param typeIds - Optional: '1' for vendor, '2' for customer, undefined for all
 */
export function useAllCategoriesForColorMap(
  typeIds?: string,
  config?: Options<CategoryListResponse, []>
) {
  const cacheKey = trackCacheKey(
    `/api/categories/all-for-color-map${typeIds ? `-type-${typeIds}` : ''}`
  );
  const { data, error, loading } = useRequest(
    () =>
      categoryApi.list({
        typeIds, // undefined = all types
        isActive: 3, // All (active + inactive)
        pageSize: -1, // Get all categories (API spec: -1 = all)
        page: 1,
      }),
    {
      cacheKey,
      staleTime: 300000, // 5 minutes - colors don't change often
      cacheTime: 300000,
      ...config,
    }
  );

  return {
    categories: data?.data ?? [],
    error,
    isLoading: loading,
  };
}

/**
 * Invalidate ALL category-related caches
 * This ensures data freshness across:
 * - Category list pages
 * - Category detail pages
 * - Category dropdowns (useCategories with activeOnly)
 * - Color maps (useAllCategoriesForColorMap used by vendor/customer lists)
 *
 * Cache keys affected:
 * - /api/categories?... (list with params)
 * - /api/categories/{id} (detail)
 * - /api/categories/all-for-color-map... (color mapping)
 */
export function invalidateAllCategoryCaches() {
  // Exact-key clear of every tracked category key — list (`?…`), detail (`/{id}`)
  // and color-map. The old predicate form was a silent no-op; see cacheKeyRegistry.ts
  clearCacheWhere((key) => key.startsWith('/api/categories'));
}

/**
 * Hook for category mutations (create, update, delete, toggle status)
 */
export function useCategoryMutations() {
  const { updateCategoryInList, removeCategory } = useCategoryStore();

  const createCategory = async (data: Parameters<typeof categoryApi.create>[0]) => {
    const response = await categoryApi.create(data);
    // Clear ALL category caches (list, detail, color map)
    invalidateAllCategoryCaches();
    return response;
  };

  const updateCategoryData = async (
    categoryId: number,
    data: Parameters<typeof categoryApi.update>[1]
  ) => {
    const response = await categoryApi.update(categoryId, data);
    updateCategoryInList(categoryId, response);
    // Clear ALL category caches (includes color map for vendor/customer pages)
    invalidateAllCategoryCaches();
    return response;
  };

  const toggleCategoryStatus = async (categoryId: number, isActive: boolean) => {
    await categoryApi.toggleStatus(categoryId, isActive);
    updateCategoryInList(categoryId, { isActive });
    // Clear ALL category caches (includes color map for vendor/customer pages)
    invalidateAllCategoryCaches();
  };

  const deleteCategoryData = async (categoryId: number) => {
    await categoryApi.delete(categoryId);
    removeCategory(categoryId);
    // Clear ALL category caches
    invalidateAllCategoryCaches();
  };

  return {
    createCategory,
    updateCategory: updateCategoryData,
    toggleStatus: toggleCategoryStatus,
    deleteCategory: deleteCategoryData,
  };
}
